<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import contactSummariesAPI from 'dashboard/api/financial/contactSummaries';
import ObligationFormModal from 'dashboard/components-next/financial/ObligationFormModal.vue';
import { dataEscritorio } from 'dashboard/helper/fusoEscritorio';

// Situação financeira do cliente na barra lateral da conversa.
//
// POR QUE (Victor, 28/08): quem atende decide o tom da conversa sabendo se o
// cliente está devendo. Antes era preciso sair do atendimento, abrir o
// Financeiro e filtrar — ninguém faz isso no meio de uma conversa.
//
// 🔴 EFICIÊNCIA: esta barra abre em TODA conversa, o dia inteiro.
//   - só busca se o usuário for admin (agente nem chega a pedir)
//   - a conta é feita no banco, não aqui: a resposta são 6 números
//   - falha em silêncio: o painel de atendimento não pode quebrar porque o
//     módulo financeiro está desligado ou fora do ar

const props = defineProps({
  contactId: { type: [Number, String], default: null },
  contactName: { type: String, default: '' },
  // Lançar cobrança só existe para quem JÁ é cliente (Victor, 17/09/2026:
  // "para quem não é cliente, não pode existir o botão").
  ehCliente: { type: Boolean, default: false },
  conversationId: { type: [Number, String], default: null },
});

const { t } = useI18n();
const router = useRouter();
const { isAdmin } = useAdmin();
const accountId = useMapGetter('getCurrentAccountId');

const resumo = ref(null);
const carregando = ref(false);

const buscar = async id => {
  // Agente comum nem pede: o servidor recusaria, e pedir de graça é consulta
  // jogada fora em toda conversa aberta.
  if (!id || !isAdmin.value) {
    resumo.value = null;
    return;
  }
  carregando.value = true;
  try {
    const { data } = await contactSummariesAPI.show(id);
    resumo.value = data?.payload || null;
  } catch {
    // Módulo desligado, sem permissão ou fora do ar: a seção some, o
    // atendimento continua.
    resumo.value = null;
  } finally {
    carregando.value = false;
  }
};

watch(() => props.contactId, buscar, { immediate: true });

const temCobranca = computed(() => Boolean(resumo.value?.has_data));
const emAtraso = computed(() => Number(resumo.value?.overdue_amount || 0) > 0);
const pastas = computed(() => resumo.value?.folders || []);

// Cliente com muitas pastas vira lista longa numa barra estreita. As em atraso
// já vêm primeiro do servidor; as demais ficam atrás de um "ver mais" para a
// informação que importa não ser empurrada para fora da tela.
const LIMITE = 3;
const verTodas = ref(false);
const pastasVisiveis = computed(() =>
  verTodas.value ? pastas.value : pastas.value.slice(0, LIMITE)
);
const ocultas = computed(() => Math.max(0, pastas.value.length - LIMITE));

const diasDeAtraso = venc => {
  if (!venc) return 0;
  const ms = Date.now() - new Date(`${venc}T00:00:00`).getTime();
  return Math.max(0, Math.floor(ms / 86400000));
};

const brl = v =>
  Number(v || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

// Fuso do ESCRITÓRIO, não do navegador: vencimento é data de cobrança e muda
// de dia se formatado no fuso de quem está olhando.
const dataCurta = valor => dataEscritorio(valor);

// 🔴 LANÇAR A COBRANÇA DE ONDE A CONVERSA ACONTECE (Victor, 17/09/2026).
//
// O advogado fecha o acordo no WhatsApp e lançava a cobrança em outra tela,
// procurando o cliente de novo. Aqui é o mesmo formulário do Financeiro —
// nenhuma segunda tela de lançar dinheiro —, aberto com o cliente já
// escolhido. O resumo acima continua sendo só leitura.
const lancando = ref(false);
// `lancando` monta o modal; `aberto` o mostra. Fechar apaga só o `aberto`: o modal sai pelo caminho de entrada e
// avisa `fechou` no fim — desmontar na hora cortava a saída (29/10/2026).
const aberto = ref(false);
const lancar = () => {
  lancando.value = true;
  aberto.value = true;
};

const prefill = computed(() => ({
  obligation_type: 'receivable',
  contact_id: props.contactId,
  contact_name: props.contactName,
  conversation_id: props.conversationId,
  // Cliente com uma pasta só não precisa escolher; com duas ou mais, fica em
  // branco para o advogado dizer qual é.
  pasta_unica: true,
}));

const aoSalvar = () => {
  aberto.value = false;
  buscar(props.contactId);
};

const irParaFinanceiro = () => {
  router.push({
    name: 'financial_index',
    params: { accountId: accountId.value },
    query: { contact_id: props.contactId },
  });
};
</script>

<template>
  <!-- Some inteiro para quem não é admin: dado financeiro do cliente não é
       informação de atendimento. -->
  <section v-if="isAdmin && !carregando && resumo" class="cfs">
    <p class="advup-rotulo advup-rotulo--secao cfs__rot">
      {{ t('CONTACT_PANEL.FINANCIAL.TITLE') }}
    </p>

    <div class="advup-card cfs__cartao">
      <!-- Total do cliente: a leitura de 1 segundo, antes do detalhe. -->
      <div class="cfs__total">
        <span
          class="ficha-pill ficha-pill--ponto"
          :class="
            emAtraso
              ? 'ficha-pill--alerta'
              : temCobranca
                ? 'ficha-pill--ok'
                : 'ficha-pill--neutro'
          "
        >
          {{
            emAtraso
              ? t('CONTACT_PANEL.FINANCIAL.OVERDUE')
              : temCobranca
                ? t('CONTACT_PANEL.FINANCIAL.UP_TO_DATE')
                : t('CONTACT_PANEL.FINANCIAL.NONE')
          }}
        </span>
        <span v-if="temCobranca" class="cfs__valor">
          {{ brl(resumo.open_amount) }}
        </span>
      </div>

      <!-- Uma seção por pasta: o dinheiro é de cada processo, não do conjunto.
           O cliente pode dever na trabalhista e estar em dia na previdenciária,
           e é sobre UM processo que a conversa acontece. -->
      <div
        v-for="p in pastasVisiveis"
        :key="p.folder_id || 'sem-pasta'"
        class="cfs__pasta"
      >
        <div class="cfs__pasta-topo">
          <i
            class="cfs__pasta-ico"
            :class="p.folder_id ? 'i-lucide-folder' : 'i-lucide-folder-x'"
            aria-hidden="true"
          />
          <span
            class="cfs__pasta-nome"
            :class="{ 'cfs__pasta-nome--sem': !p.folder_id }"
          >
            {{ p.folder_label || t('CONTACT_PANEL.FINANCIAL.NO_FOLDER') }}
          </span>
          <span
            class="ficha-pill ficha-pill--ponto cfs__selo"
            :class="
              Number(p.overdue_amount) > 0
                ? 'ficha-pill--alerta'
                : 'ficha-pill--ok'
            "
          >
            {{
              Number(p.overdue_amount) > 0
                ? t('CONTACT_PANEL.FINANCIAL.OVERDUE')
                : t('CONTACT_PANEL.FINANCIAL.UP_TO_DATE')
            }}
          </span>
        </div>

        <div v-if="Number(p.overdue_amount) > 0" class="cfs__linha">
          <span class="cfs__linha-valor cfs__linha-valor--atraso">
            {{ brl(p.overdue_amount) }}
          </span>
          <span class="cfs__linha-nota">
            {{
              t('CONTACT_PANEL.FINANCIAL.OVERDUE_FOR', {
                dias: diasDeAtraso(p.oldest_due_date),
              })
            }}
          </span>
        </div>

        <div v-if="Number(p.open_amount) > 0" class="cfs__linha">
          <span class="cfs__linha-valor">{{ brl(p.open_amount) }}</span>
          <span class="cfs__linha-nota">
            {{ t('CONTACT_PANEL.FINANCIAL.OPEN') }}
          </span>
        </div>

        <div v-if="p.last_payment" class="cfs__linha">
          <span class="cfs__linha-nota">
            {{ brl(p.last_payment.amount) }}
          </span>
          <span class="cfs__linha-nota">
            {{
              t('CONTACT_PANEL.FINANCIAL.PAID_ON', {
                data: dataCurta(p.last_payment.date),
              })
            }}
          </span>
        </div>
      </div>

      <!-- Lançar é a ÚNICA ação do bloco: botão cheio, ocupando a largura,
           e não um link do tamanho da nota de rodapé (Victor, 17/09/2026:
           "muito pequenininho aí embaixo, meio escondido"). -->
      <button
        v-if="ehCliente"
        type="button"
        class="advup-btn-forte cfs__lancar"
        @click="lancar"
      >
        <i class="i-ph-plus-circle" aria-hidden="true" />
        {{ t('CONTACT_PANEL.FINANCIAL.NEW_CHARGE') }}
      </button>

      <div class="cfs__rodape">
        <button
          v-if="ocultas > 0 && !verTodas"
          type="button"
          class="cfs__link"
          @click="verTodas = true"
        >
          {{ t('CONTACT_PANEL.FINANCIAL.SHOW_MORE', { n: ocultas }) }}
        </button>
        <button
          type="button"
          class="cfs__link cfs__link--marca"
          @click="irParaFinanceiro"
        >
          {{ t('CONTACT_PANEL.FINANCIAL.OPEN_FINANCIAL') }}
          <i class="i-lucide-arrow-right" aria-hidden="true" />
        </button>
      </div>
    </div>

    <!-- Fora do cartão: a barra tem rolagem e transformação própria, e janela
         fixa dentro de elemento transformado fica presa a ele. -->
    <Teleport v-if="lancando" to="body">
      <ObligationFormModal
        :open="aberto"
        :prefill="prefill"
        :titulo="t('CONTACT_PANEL.FINANCIAL.NEW_CHARGE')"
        @close="aberto = false"
        @fechou="lancando = false"
        @saved="aoSalvar"
      />
    </Teleport>
  </section>
</template>

<style scoped>
/* Resumo financeiro do contato — a mesma seção das outras da ficha: rótulo
 * do produto em cima e o cartão. Os selos são `.ficha-pill` (rubi é atraso,
 * teal é em dia, o mesmo significado do resto do produto). */
.cfs__rot {
  margin: 0 0 var(--esp-2);
  padding: 0 var(--esp-1);
}

.cfs__cartao {
  padding: var(--esp-3);
}

.cfs__total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--esp-2);
  padding-bottom: var(--esp-2);
  border-bottom: 1px solid rgb(var(--slate-4));
}

.cfs__valor {
  font-size: var(--txt-dado);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-12));
  font-variant-numeric: tabular-nums;
}

.cfs__pasta {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  padding: var(--esp-2) 0;
  border-bottom: 1px solid rgb(var(--slate-4));
}

.cfs__pasta:last-of-type {
  border-bottom: 0;
}

.cfs__pasta-topo {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
}

.cfs__pasta-ico {
  flex-shrink: 0;
  font-size: var(--ico-1);
  color: rgb(var(--slate-10));
}

.cfs__pasta-nome {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: var(--txt-nota);
  font-weight: var(--peso-dado);
  color: rgb(var(--slate-12));
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cfs__pasta-nome--sem {
  color: rgb(var(--slate-11));
}

.cfs__selo {
  flex-shrink: 0;
}

.cfs__linha {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--esp-2);
  font-size: var(--txt-nota);
  font-variant-numeric: tabular-nums;
}

.cfs__linha-valor {
  color: rgb(var(--slate-12));
}

.cfs__linha-valor--atraso {
  font-weight: var(--peso-forte);
  color: rgb(var(--ruby-11));
}

.cfs__linha-nota {
  color: rgb(var(--slate-11));
}

/* O botão de lançar: largura inteira e o gesto do produto (sobe 1px e a
   sombra ganha a cor). A entrada é uma vez só, ao abrir a ficha — piscar a
   cada conversa cansa quem passa o dia aqui. */
.cfs__lancar {
  width: 100%;
  margin-top: var(--esp-3);
  animation: cfsLancarChega var(--mov-3) var(--mov-curva-pulo) both;
}

.cfs__lancar > i {
  font-size: var(--ico-2);
}

@keyframes cfsLancarChega {
  from {
    opacity: 0;
    translate: 0 4px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cfs__lancar {
    animation: none;
  }
}

.cfs__rodape {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--esp-2);
  padding-top: var(--esp-2);
}

.cfs__link {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  padding: 0;
  border: 0;
  background: none;
  font-size: var(--txt-nota);
  color: rgb(var(--slate-11));
  cursor: pointer;
}

.cfs__link:hover {
  color: rgb(var(--slate-12));
}

.cfs__link--marca {
  margin-inline-start: auto;
  font-weight: var(--peso-forte);
  color: rgb(var(--blue-11));
}

.cfs__link--marca > i {
  font-size: var(--ico-1);
  transition: translate var(--mov-2) var(--mov-curva-pulo);
}

.cfs__link--marca:hover > i {
  translate: 2px 0;
}

@media (prefers-reduced-motion: reduce) {
  .cfs__link--marca > i {
    transition: none;
  }
}
</style>
